import { PointCloudOctree } from '@datalabs-jp/three-loader'
import { BlueprintV2 } from '@editor/interfaces/blueprint'
import { InspectionArea } from '@editor/interfaces/inspection'
import { resetPCD } from '@editor/services/Points'
import { LoaderParams } from '@editor/services/PotreeCache'
import { MathUtils, Matrix4, Object3D, Object3DEventMap, Quaternion, Vector3 } from 'three'

import { ProgressCallback } from '../hooks/usePCDLoader'
import { AlignedArea, AlignedAreaSerializable, LoadedPCD, LoadedPCDs } from './definitions'

/**
 * Computes the rotation (axis & angle) needed to align Plane A to Plane B.
 *
 * @param normalA Normal vector of Plane A.
 * @param normalB Normal vector of Plane B.
 */
export const getRotationToAlignPlanes = (normalA: Vector3, normalB: Vector3): Quaternion => {
  const normalizedA = normalA.clone().normalize()
  const normalizedB = normalB.clone().normalize()

  // Compute cross product (rotation axis)
  const rotationAxis = new Vector3().crossVectors(normalizedA, normalizedB)

  // Check if the planes are already aligned
  if (rotationAxis.lengthSq() < 1e-6) {
    return new Quaternion() // No rotation needed
  }

  rotationAxis.normalize()

  // Compute dot product (angle)
  const dotProduct = normalizedA.dot(normalizedB)
  const angle = Math.acos(MathUtils.clamp(dotProduct, -1, 1)) // Avoid NaN errors

  // Create a quaternion representing the rotation
  const quaternion = new Quaternion().setFromAxisAngle(rotationAxis, angle)

  return quaternion
}

/**
 * Computes a transformation matrix that aligns Object A to Object B
 * given three corresponding points on each object.
 *
 * @param leftPoints Three points on Object A.
 * @param rightPoints Three points on Object B.
 */
export const computeTransformMatrix = (leftPoints: Vector3[], rightPoints: Vector3[]): Matrix4 => {
  // Step 1: Compute Centroids
  const leftCentroid = new Vector3().add(leftPoints[0]).add(leftPoints[1]).add(leftPoints[2]).divideScalar(3)
  const rightCentroid = new Vector3().add(rightPoints[0]).add(rightPoints[1]).add(rightPoints[2]).divideScalar(3)

  const getQuaternion = (p1: Vector3, p2: Vector3, p3: Vector3): Quaternion => {
    // Step 1: Compute plane normal (Z-axis)
    const edge1 = new Vector3().subVectors(p2, p1)
    const edge2 = new Vector3().subVectors(p3, p1)
    const zAxis = new Vector3().crossVectors(edge1, edge2).normalize() // Plane normal

    // Step 2: Choose a consistent X-axis
    const xAxis = edge1.clone().normalize() // Using the edge from p1 to p2

    // Step 3: Compute Y-axis to maintain a right-handed coordinate system
    const yAxis = new Vector3().crossVectors(zAxis, xAxis).normalize()

    // Step 4: Recompute X to ensure strict orthogonality
    xAxis.crossVectors(yAxis, zAxis).normalize()

    // Step 5: Construct a rotation matrix
    const matrix = new Matrix4().makeBasis(xAxis, yAxis, zAxis)

    // Step 6: Convert the matrix into a quaternion
    const quaternion = new Quaternion().setFromRotationMatrix(matrix)

    return quaternion
  }

  const getMatrix = (p1: Vector3, p2: Vector3, p3: Vector3): Matrix4 => {
    const quaternion = getQuaternion(p1, p2, p3)

    return new Matrix4().compose(
      leftCentroid,
      quaternion,
      new Vector3(1, 1, 1), // Scale (no scaling applied)
    )
  }

  const matrixA = getMatrix(leftPoints[0], leftPoints[1], leftPoints[2])
  const matrixB = getMatrix(rightPoints[0], rightPoints[1], rightPoints[2])

  // Calculate difference in matrix
  const diffMatrix = new Matrix4().multiplyMatrices(matrixB, matrixA.clone().invert())
  diffMatrix.setPosition(new Vector3(0, 0, 0)) // Reset translation

  // Step 4: Apply rotation to centroidA
  const rotatedLeftCentroid = leftCentroid.clone().applyMatrix4(diffMatrix)

  return diffMatrix.setPosition(rightCentroid.clone().sub(rotatedLeftCentroid))
}

/**
 * Computes the relative transformation matrix from A to B.
 * That is, what transformation moves an object from A's transform to B's transform.
 *
 * @param transformA Transformation matrix of object A.
 * @param transformB Transformation matrix of object B.
 */
export const computeRelativeTransform = (transformA: Matrix4, transformB: Matrix4): Matrix4 => {
  // Compute the inverse of A
  const inverseA = new Matrix4().copy(transformA).invert()

  // Compute relative transformation: T_relative = B * A^-1
  const relativeTransform = new Matrix4().multiplyMatrices(transformB, inverseA)

  return relativeTransform
}

/**
 * Load all PCDs for a given hierarchy.
 *
 * @param hierarchy Hierarchy of areas to load PCDs for.
 * @param loader Function to load PCDs for a given area.
 * @param progress Callback to report progress.
 */
export const loadAllPCDs = (
  connections: AlignedArea[],
  parentArea: InspectionArea | null,
  loader: (loaderParams: LoaderParams, progress?: ProgressCallback) => Promise<LoadedPCD['pcd']>,
  progress?: (areaId: string, percent: number) => void,
): LoadedPCDs =>
  connections.map((connection) => {
    const params: LoaderParams = {
      inspectionArea: parentArea || connection.current.area,
      blueprint: parentArea ? connection.current.blueprint : undefined,
      blueprintAreaId:
        parentArea && parentArea.inspection_area_id !== connection.current.area.inspection_area_id
          ? connection.current.area.inspection_area_id
          : undefined,
    }

    return loader(params, (percent: number) => progress?.(connection.current.area.inspection_area_id, percent)).then(
      (pcd) => {
        if (!pcd) {
          return null
        }

        // only reset position for PCDLoader PCDs.
        resetPCD(pcd)

        // Apply the transformation
        pcd.applyMatrix4(connection.matrix)

        return {
          areaId: connection.current.area.inspection_area_id,
          pcd,
        }
      },
    )
  })

/**
 * Collects all connected areas starting from a given area.
 *
 * @param parent Starting area.
 * @param area The area to check for connections.
 * @param areas List of all areas.
 * @returns
 */
export const collectConnectedAreas = (
  parent: AlignedArea,
  area: InspectionArea,
  areas: InspectionArea[],
  blueprint?: BlueprintV2,
): AlignedArea[] => {
  const alignments: AlignedArea[] = []

  // Just push the starting area.
  let current = parent
  if (parent.current.area.inspection_area_id === area.inspection_area_id) {
    alignments.push(parent)
  } else {
    // Deleted alignments can result in empty object.
    if (Object.keys(area.pcd_alignments || {}).length === 0) return alignments

    const alignment = area.pcd_alignments![parent.current.area.inspection_area_id]
    const ownMatrix = new Matrix4().fromArray(alignment.transformation)
    const isBase = alignment.base_area_id === area.inspection_area_id
    if (isBase) {
      ownMatrix.invert()
    }

    const matrix = parent.matrix.clone().multiply(ownMatrix)
    const baseAnchors = alignment.base_anchor_positions.map((p) =>
      new Vector3(...p).applyMatrix4(isBase ? matrix : parent.matrix),
    )
    const baseCenter = baseAnchors.reduce((acc, pos) => acc.add(pos), new Vector3(0, 0, 0)).divideScalar(3)
    const targetAnchors = alignment.target_anchor_positions.map((p) =>
      new Vector3(...p).applyMatrix4(isBase ? parent.matrix : matrix),
    )
    const targetCenter = targetAnchors.reduce((acc, pos) => acc.add(pos), new Vector3(0, 0, 0)).divideScalar(3)

    current = {
      current: {
        area,
        blueprint,
        anchors: isBase ? baseAnchors : targetAnchors,
        center: isBase ? baseCenter : targetCenter,
      },
      other: {
        area: parent.current.area,
        anchors: isBase ? targetAnchors : baseAnchors,
        center: isBase ? targetCenter : baseCenter,
      },
      matrix,
      inverted: isBase,
    }
    alignments.push(current)
  }

  Object.keys(area.pcd_alignments || {})
    .filter((ar) => ar !== parent.current.area.inspection_area_id)
    .forEach((ar) => {
      const child = areas.find((a) => a.inspection_area_id === ar)
      if (!child || child === area) return // prevent infinite loop

      const connectedAreas = collectConnectedAreas(current, child, areas, blueprint)
      alignments.push(...connectedAreas)
    })

  return alignments
}

/**
 * Converts a list of aligned areas to a serializable format.
 *
 * @param areas List of aligned areas.
 */
export const connectedAreasToSerializable = (areas: AlignedArea[]): AlignedAreaSerializable[] =>
  areas.map((area) => ({
    current: {
      area: area.current.area,
      blueprint: area.current.blueprint,
      anchors: area.current.anchors.map((a) => a.toArray()),
      center: area.current.center.toArray(),
    },
    other: area.other
      ? {
          area: area.other.area,
          anchors: area.other.anchors.map((a) => a.toArray()),
          center: area.other.center.toArray(),
        }
      : undefined,
    matrix: area.matrix.toArray(),
    inverted: area.inverted,
  }))

/**
 * Converts a list of serializable aligned areas to a list of aligned areas.
 *
 * @param areas List of serializable aligned areas.
 */
export const serializableToConnectedAreas = (areas?: AlignedAreaSerializable[]): AlignedArea[] =>
  areas?.map((area) => ({
    current: {
      area: area.current.area,
      blueprint: area.current.blueprint,
      anchors: area.current.anchors.map((a) => new Vector3(...a)),
      center: new Vector3(...area.current.center),
    },
    other: area.other
      ? {
          area: area.other.area,
          anchors: area.other.anchors.map((a) => new Vector3(...a)),
          center: new Vector3(...area.other.center),
        }
      : undefined,
    matrix: new Matrix4().fromArray(area.matrix),
    inverted: area.inverted,
  })) || []

/**
 * Gets the area ID from the object, stored in the name property.
 *
 * @param object The object to get the area ID from.
 * @returns
 */
export const getObjectAreaId = (object: Object3D<Object3DEventMap>): string => {
  // For both PCDLoader and PointCloudOctree, 'intersectedPoint' will be Points instance.
  // But the name is stored differently.

  // For Potree, it is stored on a parent PointCloudOctree instance.
  // But it could be several layers up so we need to traverse the parent.
  let { parent } = object
  while (parent && !(parent instanceof PointCloudOctree)) {
    parent = parent.parent
  }

  if (parent) return parent.name

  // For PCDLoader, it's stored directly on the Points instance.
  return object.name
}
